import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import * as moment from 'moment';
import { of } from 'rxjs';
import { catchError, filter, first, map, switchMap, withLatestFrom } from 'rxjs/operators';

import { RocService } from '@app/services/roc.service';
import {
  loadRocReport,
  loadRocReportError,
  loadRocReportSuccess,
  updateConstructionCost,
  updateConstructionCostSuccess,
  updateMarketRent,
  updateMarketRentSuccess,
  updateNewRent,
  updateNewRentSuccess,
  updateOldRent,
  updateOldRentSuccess,
} from '@main-application/dashboard-reports/roc-report/store/roc.actions';
import { TimezoneService } from '@main-application/management/components/system-configuration/components/date-time-configuration/timezone.service';
import { SnackbarErrorMessage } from '@ui-components/components/customized-snackbar/snackbar-message.enum';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';
import { environment } from 'src/environments/environment';
import { EnvironmentType } from 'src/environments/environment-config';

import { selectRocFilter, selectRocFilterSet, setInitialDateData, setInitialDateDataSuccess } from '.';

@Injectable()
export class RocEffects {
  loadRocReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadRocReport),
      switchMap(action =>
        this.store.select(selectRocFilter).pipe(
          filter(f => !!f.beginDate && !!f.endDate),
          first(),
          map(filter => ({
            action,
            filter,
          }))
        )
      ),
      switchMap(({ action, filter }) =>
        this.rocService.fetchReport(action.portfolioId, filter).pipe(
          map(taskList => loadRocReportSuccess({ report: taskList || [] })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.T01RetrievingTasks);
            return of(loadRocReportError());
          })
        )
      )
    )
  );

  updateMarketRent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateMarketRent),
      switchMap(action =>
        this.rocService.updateMarketRent(action.turnoverId, action.value).pipe(
          map(() => updateMarketRentSuccess({ ...action })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.T01RetrievingTasks);
            return of(loadRocReport({ portfolioId: action.portfolioId }));
          })
        )
      )
    )
  );

  updateConstructionCost$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateConstructionCost),
      switchMap(action =>
        this.rocService.updateConstructionCost(action.turnoverId, action.value).pipe(
          map(() => updateConstructionCostSuccess({ ...action })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.T01RetrievingTasks);
            return of(loadRocReport({ portfolioId: action.portfolioId }));
          })
        )
      )
    )
  );

  updateNewRent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateNewRent),
      switchMap(action =>
        this.rocService.updateNewRent(action.turnoverId, action.value).pipe(
          map(() => updateNewRentSuccess({ ...action })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.T01RetrievingTasks);
            return of(loadRocReport({ portfolioId: action.portfolioId }));
          })
        )
      )
    )
  );

  updateOldRent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateOldRent),
      switchMap(action =>
        this.rocService.updateOldRent(action.turnoverId, action.value).pipe(
          map(() => updateOldRentSuccess({ ...action })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.T01RetrievingTasks);
            return of(loadRocReport({ portfolioId: action.portfolioId }));
          })
        )
      )
    )
  );

  setInitialDateData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setInitialDateData),
      withLatestFrom(this.store.select(selectRocFilterSet)),
      filter(([_action, corrected]) => !corrected),
      switchMap(() =>
        this.store.select(selectRocFilter).pipe(
          first(),
          this.timezoneService.addTimezoneForEntity((filter, _tz) => ({
            ...filter,
            beginDate:
              environment.type === EnvironmentType.Demo
                ? new Date('12/1/2022')
                : moment(this.timezoneService.getCurrentDate()).add(-3, 'month').startOf('day').toDate(),
            endDate: moment(this.timezoneService.getCurrentDate()).endOf('day').toDate(),
          })),
          map(filter => setInitialDateDataSuccess({ filter }))
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private rocService: RocService,
    private store: Store<{}>,
    private snackbarService: SnackbarService,
    private timezoneService: TimezoneService
  ) {}
}
